import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  where,
  type Timestamp,
} from 'firebase/firestore';
import { appConfig } from '@/config/app';
import { getFirestoreDb } from '@/services/firebase';
import {
  buildTimestampPayload,
  timestampToDate,
} from '@/modules/inventory/services/firestoreMapper';
import { RESERVATIONS_COLLECTION } from '@/modules/reservations/services/constants';
import type {
  Reservation,
  ReservationInput,
  ReservationStatus,
} from '@/modules/reservations/types';
import { env } from '@/config/env';
import {
  cancelReservationViaWorker,
  createReservationViaWorker,
  isWorkerReservationsEnabled,
  updateReservationViaWorker,
} from '@/services/api/reservations.api';
import {
  publishReservationCancelled,
  publishReservationCreated,
} from '@/services/events/eventPublisher.service';
import { recordEntityWrite, toAuditSnapshot } from '@/services/audit';
import { withFirestoreError } from '@/shared/utils/firestoreError';

type ReservationFirestoreDoc = {
  companyId: string;
  title: string;
  description: string;
  inventoryItemIds: string[];
  clientName: string;
  startDate: string;
  endDate: string;
  status: ReservationStatus;
  notes: string;
  createdBy: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
};

function mapReservation(id: string, data: ReservationFirestoreDoc): Reservation {
  return {
    id,
    companyId: data.companyId,
    title: data.title,
    description: data.description ?? '',
    inventoryItemIds: data.inventoryItemIds ?? [],
    clientName: data.clientName ?? '',
    startDate: data.startDate,
    endDate: data.endDate,
    status: data.status,
    notes: data.notes ?? '',
    createdBy: data.createdBy,
    createdAt: timestampToDate(data.createdAt),
    updatedAt: timestampToDate(data.updatedAt),
  };
}

export async function listReservations(
  companyId: string = appConfig.companyId,
): Promise<Reservation[]> {
  const q = query(
    collection(getFirestoreDb(), RESERVATIONS_COLLECTION),
    where('companyId', '==', companyId),
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => mapReservation(d.id, d.data() as ReservationFirestoreDoc))
    .sort((a, b) => b.startDate.localeCompare(a.startDate));
}

/**
 * Reservas que intersectan [rangeStart, rangeEnd] (fechas ISO inclusivas).
 * Requiere índice compuesto `companyId` + `startDate`.
 */
export async function listReservationsInRange(
  rangeStart: string,
  rangeEnd: string,
  companyId: string = appConfig.companyId,
): Promise<Reservation[]> {
  const q = query(
    collection(getFirestoreDb(), RESERVATIONS_COLLECTION),
    where('companyId', '==', companyId),
    where('startDate', '<=', rangeEnd),
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => mapReservation(d.id, d.data() as ReservationFirestoreDoc))
    .filter((r) => r.endDate >= rangeStart)
    .sort((a, b) => a.startDate.localeCompare(b.startDate));
}

/** Reservas futuras o en curso que incluyen un ítem (filtro en cliente). */
export async function listReservationsForItem(
  itemId: string,
  rangeStart?: string,
  rangeEnd?: string,
): Promise<Reservation[]> {
  const start = rangeStart ?? '1970-01-01';
  const end = rangeEnd ?? '2999-12-31';
  const inRange = await listReservationsInRange(start, end);
  return inRange.filter((r) => r.inventoryItemIds.includes(itemId));
}

export async function getReservation(id: string): Promise<Reservation | null> {
  const snap = await getDoc(
    doc(getFirestoreDb(), RESERVATIONS_COLLECTION, id),
  );
  if (!snap.exists()) return null;
  return mapReservation(snap.id, snap.data() as ReservationFirestoreDoc);
}

export async function saveReservation(
  input: ReservationInput,
  createdBy: string,
  id?: string,
  expectedUpdatedAt?: string,
): Promise<Reservation> {
  if (env.useWorkerReservations && isWorkerReservationsEnabled()) {
    if (id) {
      return updateReservationViaWorker(id, input, expectedUpdatedAt);
    }
    const created = await createReservationViaWorker(input);
    return created;
  }

  return withFirestoreError(async () => {
  const ref = id
    ? doc(getFirestoreDb(), RESERVATIONS_COLLECTION, id)
    : doc(collection(getFirestoreDb(), RESERVATIONS_COLLECTION));
  const existing = await getDoc(ref);
  const before = existing.exists()
    ? toAuditSnapshot(
        mapReservation(existing.id, existing.data() as ReservationFirestoreDoc),
      )
    : undefined;
  const payload = {
    companyId: appConfig.companyId,
    title: input.title.trim(),
    description: input.description.trim(),
    inventoryItemIds: input.inventoryItemIds,
    clientName: input.clientName.trim(),
    startDate: input.startDate,
    endDate: input.endDate,
    status: input.status,
    notes: input.notes.trim(),
    createdBy: existing.exists()
      ? (existing.data() as ReservationFirestoreDoc).createdBy
      : createdBy,
    ...buildTimestampPayload(!existing.exists()),
  };
  await setDoc(ref, payload, { merge: true });
  const saved = await getDoc(ref);
  if (!saved.exists()) throw new Error('No se pudo guardar la reserva.');
  const result = mapReservation(saved.id, saved.data() as ReservationFirestoreDoc);
  const isCreate = !existing.exists();
  recordEntityWrite({
    entityType: 'reservation',
    entityId: result.id,
    isCreate,
    ...(before !== undefined ? { before } : {}),
    after: toAuditSnapshot(result),
  });
  if (isCreate) {
    void publishReservationCreated(
      {
        reservationId: result.id,
        title: result.title,
        status: result.status,
        startDate: result.startDate,
        endDate: result.endDate,
      },
      `reservation.created:${result.id}`,
    );
  } else if (result.status === 'cancelled' && before?.status !== 'cancelled') {
    void publishReservationCancelled(
      {
        reservationId: result.id,
        title: result.title,
        status: result.status,
        startDate: result.startDate,
        endDate: result.endDate,
      },
      `reservation.cancelled:${result.id}`,
    );
  }
  return result;
  }, 'reservation');
}

export async function cancelReservation(id: string): Promise<Reservation> {
  if (env.useWorkerReservations && isWorkerReservationsEnabled()) {
    return cancelReservationViaWorker(id);
  }

  const existing = await getReservation(id);
  if (!existing) throw new Error('Reserva no encontrada.');
  return saveReservation(
    {
      title: existing.title,
      description: existing.description,
      inventoryItemIds: existing.inventoryItemIds,
      clientName: existing.clientName,
      startDate: existing.startDate,
      endDate: existing.endDate,
      status: 'cancelled',
      notes: existing.notes,
    },
    existing.createdBy,
    id,
  );
}
